import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg } from "@/lib/api";

const DEFAULT_TIERS = [{ days_before: 30, refund_pct: 100 }, { days_before: 14, refund_pct: 50 }, { days_before: 0, refund_pct: 0 }];

export function PolicyTab({ data, api, reload }) {
  const l = data.listing;
  const [rule, setRule] = useState(data.refund_rule || { tiers: DEFAULT_TIERS, no_show_refund_pct: 0, policy_text: "Refunds follow the tiers below, on the experience price. The GOSHA service fee is non-refundable." });
  const [alloc, setAlloc] = useState(data.payout_config?.allocations || [{ party: "organizer", name: l.host_stack?.organizer?.name || "", pct: 70 }, { party: "operator", name: l.host_stack?.operator?.name || "", pct: 30 }]);
  const total = alloc.reduce((s, a) => s + Number(a.pct || 0), 0);
  const updT = (i, k, v) => setRule({ ...rule, tiers: rule.tiers.map((t, j) => (j === i ? { ...t, [k]: Number(v) } : t)) });
  const updA = (i, k, v) => setAlloc(alloc.map((a, j) => (j === i ? { ...a, [k]: k === "pct" ? Number(v) : v } : a)));

  const saveRule = async () => { try { await api.put(`/partner/listings/${l.id}/refund-rule`, rule); toast.success("Policy saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const saveAlloc = async () => { try { await api.put(`/partner/listings/${l.id}/payout-config`, { allocations: alloc }); toast.success("Payout allocation saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="grid gap-10 lg:grid-cols-2" data-testid="policy-tab">
      <div className="space-y-4">
        <h3 className="text-xl text-primary">Cancellation & no-show rule</h3>
        {rule.tiers.map((t, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <Input type="number" className="w-24" value={t.days_before} onChange={(e) => updT(i, "days_before", e.target.value)} data-testid={`tier-days-${i}`} /> days+ before →
            <Input type="number" className="w-24" value={t.refund_pct} onChange={(e) => updT(i, "refund_pct", e.target.value)} data-testid={`tier-pct-${i}`} /> % refund
            <Button size="icon" variant="ghost" onClick={() => setRule({ ...rule, tiers: rule.tiers.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRule({ ...rule, tiers: [...rule.tiers, { days_before: 7, refund_pct: 25 }] })} data-testid="tier-add-btn"><Plus className="mr-1 h-3 w-3" />Add tier</Button>
        <div className="flex items-center gap-2 text-sm">No-show refund <Input type="number" className="w-24" value={rule.no_show_refund_pct} onChange={(e) => setRule({ ...rule, no_show_refund_pct: Number(e.target.value) })} data-testid="no-show-pct" /> %</div>
        <div><Label className="text-xs text-muted-foreground">Policy text shown to guests</Label><Textarea className="mt-1" value={rule.policy_text} onChange={(e) => setRule({ ...rule, policy_text: e.target.value })} data-testid="policy-text" /></div>
        <Button className="rounded-full" onClick={saveRule} data-testid="policy-save-btn">Save policy</Button>
      </div>
      <div className="space-y-4">
        <h3 className="text-xl text-primary">Payout allocation</h3>
        <p className="text-sm text-muted-foreground">Listing-level split of the net payout (after GOSHA's commission). One operator per listing.</p>
        {alloc.map((a, i) => (
          <div key={i} className="grid grid-cols-[130px_1fr_80px_auto] gap-2">
            <Select value={a.party} onValueChange={(v) => updA(i, "party", v)}>
              <SelectTrigger data-testid={`alloc-party-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{["organizer", "operator", "curator"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={a.name} placeholder="Payee name" onChange={(e) => updA(i, "name", e.target.value)} data-testid={`alloc-name-${i}`} />
            <Input type="number" value={a.pct} onChange={(e) => updA(i, "pct", e.target.value)} data-testid={`alloc-pct-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => setAlloc(alloc.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAlloc([...alloc, { party: "curator", name: "", pct: 0 }])} data-testid="alloc-add-btn"><Plus className="mr-1 h-3 w-3" />Add payee</Button>
          <span className={`text-sm font-semibold ${total === 100 ? "text-primary" : "text-destructive"}`} data-testid="alloc-total">{total}%</span>
        </div>
        <Button className="rounded-full" onClick={saveAlloc} disabled={total !== 100} data-testid="alloc-save-btn">Save allocation</Button>
      </div>
    </div>
  );
}

export function PublishTab({ data, api, reload }) {
  const l = data.listing;
  const ready = data.compliance.every((c) => c.ok);
  const submit = async () => { try { await api.post(`/partner/listings/${l.id}/submit`); toast.success("Submitted for GOSHA review"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-2xl" data-testid="publish-tab">
      <h3 className="text-xl text-primary">Compliance checklist</h3>
      <p className="text-sm text-muted-foreground">Every item must pass before submitting. GOSHA ops review the listing before it goes live.</p>
      <ul className="mt-5 divide-y divide-border rounded-xl border border-border bg-white">
        {data.compliance.map((c) => (
          <li key={c.key} className="flex items-center gap-3 p-4 text-sm" data-testid={`compliance-${c.key}`}>
            {c.ok ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
            <span className={c.ok ? "" : "text-muted-foreground"}>{c.label}</span>
          </li>
        ))}
      </ul>
      {l.compliance_notes && <p className="mt-4 rounded-lg bg-muted p-3 text-sm" data-testid="moderation-notes">Ops note: {l.compliance_notes}</p>}
      <div className="mt-6 flex items-center gap-4">
        <Button className="rounded-full bg-secondary px-8 hover:bg-secondary/90" disabled={!ready || !["draft", "rejected"].includes(l.status)} onClick={submit} data-testid="submit-for-review-btn">Submit for review</Button>
        <span className="text-sm capitalize text-muted-foreground" data-testid="listing-status">Status: {l.status.replace("_", " ")}</span>
      </div>
    </div>
  );
}
